<template>
  <ion-page>
    <div class="dashboard-layout">
      <!-- Sidebar Navigation with its own background -->
      <div class="sidebar-container">
        <SidebarNavigation @close-sidebar="closeMobileSidebar" />
      </div>
      
      <!-- Main Dashboard Content with its own background -->
      <div class="main-dashboard-content">
        <!-- Mobile Sidebar Backdrop -->
        <div 
          v-if="mobileSidebarOpen" 
          class="mobile-sidebar-backdrop"
          @click="toggleMobileSidebar"
        ></div>
        
        <ion-header class="ion-no-border">
          <!-- Mobile Menu Toggle Button -->
          <ion-button 
            class="mobile-menu-toggle"
            fill="clear" 
            @click="toggleMobileSidebar"
          >
            <ion-icon :icon="menuOutline" slot="icon-only"></ion-icon>
          </ion-button>
        </ion-header>
        
        <ion-content scroll-y="true" :fullscreen="false">
          <!-- Loading Spinner Overlay -->
          <ion-loading
            v-if="loading"
            :is-open="loading"
            message="Loading profile..."
            spinner="crescent"
            css-class="dashboard-loading-overlay"
          />
          
          
          <div class="dashboard-content">
            <div class="stats-grid">
              <div class="glass-card stat-card">
                <ion-icon :icon="walletOutline" class="stat-icon"></ion-icon>
                <div class="stat-content">
                  <h3>Monthly Balance</h3>
                  <p class="stat-value">{{ monthlyBalance }}</p>
                </div>
              </div>

              <div class="glass-card stat-card">
                <ion-icon :icon="walletOutline" class="stat-icon"></ion-icon>
                <div class="stat-content">
                  <h3>Savings</h3>
                  <p class="stat-value positive">{{ monthlySavings }}</p>
                </div>
              </div>

              <div class="glass-card stat-card">
                <ion-icon :icon="trendingUpOutline" class="stat-icon"></ion-icon>
                <div class="stat-content">
                  <h3>Income</h3>
                  <p class="stat-value positive">{{ monthlyIncome }}</p>
                </div>
              </div>

              <div class="glass-card stat-card">
                <ion-icon :icon="trendingDownOutline" class="stat-icon"></ion-icon>
                <div class="stat-content">
                  <h3>Expenses</h3>
                  <p class="stat-value negative">{{ monthlyExpenses }}</p>
                </div>
              </div>
            </div>

            <div class="charts-row">
              <div class="glass-card chart-card">
                <h3 class="chart-title">Income vs Expenses</h3>
                <div class="chart-container">
                  <Pie
                    ref="pieChart"
                    :key="pieChartKey"
                    :data="chartData"
                    :options="chartOptions"
                  />
                </div>
              </div>

              <div class="glass-card scenarios-card">
                <h3 class="chart-title">Scenario Toggles</h3>
                <div class="scenarios-container">
                  <div class="scenarios-header">
                    <ion-icon :icon="layersOutline" class="scenarios-icon"></ion-icon>
                    <span class="scenarios-subtitle">Combine scenarios to include their events in calculations</span>
                  </div>
                  <div class="scenarios-list">
                    <div class="scenario-item default-scenario">
                      <ion-label class="scenario-label">Base Budget</ion-label>
                      <ion-icon :icon="checkmarkCircle" class="default-scenario-icon"></ion-icon>
                    </div>
                    <div 
                      v-for="scenario in availableScenarios" 
                      :key="`${currentProfile?.id}-${scenario.id}`"
                      class="scenario-item"
                    >
                      <ion-label class="scenario-label">{{ scenario.name }}</ion-label>
                      <ion-toggle 
                        :checked="activeScenarios.has(scenario.id)" 
                        @ionChange="toggleScenario(scenario)"
                      ></ion-toggle>
                    </div>
                    <div v-if="availableScenarios.length === 0" class="no-scenarios">
                      <ion-label>No additional scenarios available</ion-label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="glass-card chart-card">
                <h3 class="chart-title">Expense Categories</h3>
                <div class="chart-container">
                  <Pie
                    ref="pieChart1"
                    :key="pieChartKey + 1"
                    :data="expenseCategoriesData"
                    :options="chartOptions"
                  />
                </div>
              </div>
          </div>
          
          <!-- Date Range Filter -->
          <div class="date-range-filters">
            <div class="date-range-row">
              <div class="date-field">
                <ion-label class="date-label">Start Date</ion-label>
                <div class="date-selects">
                  <ion-select 
                    ref="startMonthSelect"
                    v-model="startMonth" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="month in months" :key="month.value" :value="month.value" :selected="startMonth === month.value">
                      {{ month.label }}
                    </ion-select-option>
                  </ion-select>
                  
                  <ion-select 
                    v-model="startDay" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="day in availableDays" :key="day" :value="day" :selected="startDay === day">
                      {{ day }}
                    </ion-select-option>
                  </ion-select>
                  
                  <ion-select 
                    v-model="startYear" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="year in years" :key="year" :value="year" :selected="startYear === year">
                      {{ year }}
                    </ion-select-option>
                  </ion-select>
                </div>
              </div>
              
              <div class="date-field">
                <ion-label class="date-label">End Date</ion-label>
                <div class="date-selects">
                  <ion-select 
                    ref="endMonthSelect"
                    v-model="endMonth" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="month in months" :key="month.value" :value="month.value" :selected="endMonth === month.value">
                      {{ month.label }}
                    </ion-select-option>
                  </ion-select>
                  
                  <ion-select 
                    v-model="endDay" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="day in availableEndDays" :key="day" :value="day" :selected="endDay === day">
                      {{ day }}
                    </ion-select-option>
                  </ion-select>
                  
                  <ion-select 
                    v-model="endYear" 
                    class="glass-input date-select"
                    @ionChange="updateFilteredData"
                    interface="popover"
                    :interfaceOptions="{ cssClass: 'custom-popover' }"
                  >
                    <ion-select-option v-for="year in years" :key="year" :value="year" :selected="endYear === year">
                      {{ year }}
                    </ion-select-option>
                  </ion-select>
                </div>
              </div>
            </div>
            
          </div>
          
          <!-- Transaction History Table -->
          <div class="transaction-history-section">
            <div class="table-header">
              <h3>Transaction History</h3>
              <ion-button 
                size="small" 
                fill="clear" 
                @click="toggleTransactionHistory"
                class="hide-button"
              >
                <ion-icon :icon="transactionHistoryExpanded ? chevronUp : chevronDown"></ion-icon>
                {{ transactionHistoryExpanded ? 'Hide' : 'Show' }}
              </ion-button>
            </div>
            
            <div v-if="transactionHistoryExpanded" class="table-content">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="event in sortedFilteredEvents" :key="`${currentProfile?.id}-${event.id || event._id}-${event.date}`">
                    <td>{{ formatDate(event.date) }}</td>
                    <td>{{ event.name || event.description || 'N/A' }}</td>
                    <td>{{ event.category || 'N/A' }}</td>
                    <td>{{ event.type }}</td>
                    <td :class="event.type === 'CREDIT' ? 'positive' : 'negative'">
                      {{ formatCurrency(getEventDisplayAmount(event)) }}
                    </td>
                  </tr>
                  <tr v-if="sortedFilteredEvents.length === 0">
                    <td colspan="5" class="no-data">
                      No transactions found for the selected date range
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </ion-content>
      </div>
    </div>
  </ion-page>
</template>

<script>
import { IonPage, IonContent, IonButton, IonIcon, IonHeader, IonToolbar, IonButtons, IonTitle, IonLoading, IonLabel, IonCheckbox, IonToggle, IonSelect, IonSelectOption } from '@ionic/vue';
import { 
  walletOutline,
  trendingUpOutline,
  trendingDownOutline,
  layersOutline,
  chevronUp,
  chevronDown,
  checkmarkCircle,
  menuOutline,
} from 'ionicons/icons';
import { Pie } from 'vue-chartjs'
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js'
import { useProfileStore } from '@/store/profile';
import { useScenariosStore } from '@/store/scenarios';
import { useEventsStore } from '@/store/events';
import { mapState, mapActions } from 'pinia';
import SidebarNavigation from '@/components/SidebarNavigation.vue';
import axios from 'axios';
import {getAPIURL} from '../logic/api';

ChartJS.register(ArcElement, Tooltip, Legend)

export default {
  name: 'DashboardPage',
  components: {
    IonPage,
    IonContent,
    IonButton,
    IonIcon,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonTitle,
    IonLoading,
    IonLabel,
    IonCheckbox,
    IonToggle,
    IonSelect,
    IonSelectOption,
    Pie,
    SidebarNavigation
  },
  setup() {
    const profileStore = useProfileStore();
    const scenariosStore = useScenariosStore();
    const eventsStore = useEventsStore();
    return { profileStore, scenariosStore, eventsStore };
  },
  data() {
    return {
      loading: false,
      error: null,
      watchersDisabled: false,
      lastProfileChange: 0,
      lastDataUpdate: 0,
      pieChartKey: 0,
      activeScenarios: new Set(['default']),
      combinedActiveEvents: [],
      startMonth: null,
      startDay: null,
      startYear: null,
      endMonth: null,
      endDay: null,
      endYear: null,
      transactionHistoryExpanded: true,
      mobileSidebarOpen: false,
      profileDataLoaded: false,
      dashboardDataLogged: false,
      months: [
        { value: 0, label: 'January' },
        { value: 1, label: 'February' },
        { value: 2, label: 'March' },
        { value: 3, label: 'April' },
        { value: 4, label: 'May' },
        { value: 5, label: 'June' },
        { value: 6, label: 'July' },
        { value: 7, label: 'August' },
        { value: 8, label: 'September' },
        { value: 9, label: 'October' },
        { value: 10, label: 'November' },
        { value: 11, label: 'December' }
      ],
      years: Array.from({ length: 60 }, (_, i) => new Date().getFullYear() - 20 + i),
      walletOutline,
      trendingUpOutline,
      trendingDownOutline,
      layersOutline,
      chevronUp,
      chevronDown,
      checkmarkCircle,
      menuOutline,
      categoryColors: {
        'Food & Dining': { bg: 'rgba(255, 182, 193, 0.7)', border: 'rgba(255, 182, 193, 0.9)' },
        'Transportation': { bg: 'rgba(173, 216, 230, 0.7)', border: 'rgba(173, 216, 230, 0.9)' },
        'Mortgage': { bg: '#90EE90', border: '#90EE90' },
        'Utilities': { bg: '#00D2D3', border: '#00D2D3' },
        'Utility': { bg: '#00D2D3', border: '#00D2D3' },
        'Healthcare': { bg: 'rgba(221, 160, 221, 0.7)', border: 'rgba(221, 160, 221, 0.9)' },
        'Entertainment': { bg: 'rgba(255, 218, 185, 0.7)', border: 'rgba(255, 218, 185, 0.9)' },
        'Shopping': { bg: 'rgba(230, 230, 250, 0.7)', border: 'rgba(230, 230, 250, 0.9)' },
        'Education': { bg: 'rgba(240, 248, 255, 0.7)', border: 'rgba(240, 248, 255, 0.9)' },
        'Insurance': { bg: 'rgba(255, 192, 203, 0.7)', border: 'rgba(255, 192, 203, 0.9)' },
        'Taxes': { bg: 'rgba(152, 251, 152, 0.7)', border: 'rgba(152, 251, 152, 0.9)' },
        'Debt Payment': { bg: 'rgba(255, 160, 122, 0.7)', border: 'rgba(255, 160, 122, 0.9)' },
        'Savings': { bg: '#FFB6C1', border: '#FFB6C1' },
        'Investment': { bg: 'rgba(175, 238, 238, 0.7)', border: 'rgba(175, 238, 238, 0.9)' },
        'Gifts': { bg: 'rgba(255, 182, 193, 0.7)', border: 'rgba(255, 182, 193, 0.9)' },
        'Travel': { bg: 'rgba(255, 218, 185, 0.7)', border: 'rgba(255, 218, 185, 0.9)' },
        'Personal Care': { bg: 'rgba(255, 192, 203, 0.7)', border: 'rgba(255, 192, 203, 0.9)' },
        'Pets': { bg: 'rgba(210, 180, 140, 0.7)', border: 'rgba(210, 180, 140, 0.9)' },
        'Home Improvement': { bg: 'rgba(188, 143, 143, 0.7)', border: 'rgba(188, 143, 143, 0.9)' },
        'Legal': { bg: 'rgba(211, 211, 211, 0.7)', border: 'rgba(211, 211, 211, 0.9)' },
        'Other': { bg: 'rgba(220, 220, 220, 0.7)', border: 'rgba(220, 220, 220, 0.9)' },
        'Phone': { bg: 'rgba(176, 196, 222, 0.7)', border: 'rgba(176, 196, 222, 0.9)' },
        'Auto_loan': { bg: '#CD5C5C', border: '#CD5C5C' }
      },
      fallbackColors: [
        { bg: '#FFB6C1', border: '#FFB6C1' },
        { bg: '#98FB98', border: '#98FB98' },
        { bg: '#87CEEB', border: '#87CEEB' },
        { bg: '#DDA0DD', border: '#DDA0DD' },
        { bg: '#F0E68C', border: '#F0E68C' },
        { bg: '#FFA07A', border: '#FFA07A' },
        { bg: '#B0E0E6', border: '#B0E0E6' },
        { bg: '#FFC0CB', border: '#FFC0CB' },
        { bg: '#90EE90', border: '#90EE90' },
        { bg: '#E6E6FA', border: '#E6E6FA' }
      ]
    }
  },
  computed: {
    ...mapState(useProfileStore, ['profiles', 'currentProfile']),
    ...mapState(useScenariosStore, ['allScenarios', 'selectedScenario', 'defaultScenario', 'isDefaultScenario', 'customScenarios', 'hasScenarios', 'isLoading', 'hasError']),
    ...mapState(useEventsStore, ['allEvents', 'monthlyEvents', 'filteredEvents', 'activeEvents', 'cashFlowInTotal', 'cashFlowOutTotal']),
    
    // Helper method to get the correct amount for an event (monthly payment for loans, regular amount for others)
    getEventDisplayAmount() {
      return (event) => {
        // If the amount is already calculated (from calendar store), use it directly
        if (event.amount !== undefined && event.amount !== null) {
          return event.amount;
        }
        
        // Fallback to calculating from monthly_payment and escrow
        const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
        if (loanCategories.includes(event.category) && event.monthly_payment && event.monthly_payment > 0) {
          // For mortgages, add escrow to the monthly payment
          if (event.category === 'MORTGAGE' && event.escrow && event.escrow > 0) {
            return parseFloat(event.monthly_payment) + parseFloat(event.escrow);
          }
          return event.monthly_payment;
        }
        return event.amount;
      };
    },
    availableScenarios() {
      return this.customScenarios;
    },
    availableDays() {
      const month = this.startMonth !== null ? this.startMonth : (this.endMonth !== null ? this.endMonth : new Date().getMonth());
      const year = this.startYear !== null ? this.startYear : (this.endYear !== null ? this.endYear : new Date().getFullYear());
      const lastDay = new Date(year, month + 1, 0).getDate();
      return Array.from({ length: lastDay }, (_, i) => i + 1);
    },
    availableEndDays() {
      const month = this.endMonth !== null ? this.endMonth : new Date().getMonth();
      const year = this.endYear !== null ? this.endYear : new Date().getFullYear();
      const lastDay = new Date(year, month + 1, 0).getDate();
      return Array.from({ length: lastDay }, (_, i) => i + 1);
    },
    sortedFilteredEvents() {
      const eventsToUse = this.combinedActiveEvents.length > 0 ? this.combinedActiveEvents : this.filteredEvents;
      if (!eventsToUse || !Array.isArray(eventsToUse)) {
        return [];
      }
      const events = [...eventsToUse];
      return events.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });
    },
    toTitleCase() {
      return (str) => {
        if (!str) return '';
        return str
          .toLowerCase()
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      };
    },
    monthlyBalance() {
      return this.formatCurrency(this.filteredBalance);
    },
    monthlySavings() {
      return this.formatCurrency(this.filteredSavings > 0 ? this.filteredSavings : 0);
    },
    monthlyIncome() {
      return this.formatCurrency(this.filteredIncome);
    },
    monthlyExpenses() {
      return this.formatCurrency(this.filteredExpenses);
    },
    filteredIncome() {
      const eventsToUse = this.combinedActiveEvents.length > 0 ? this.combinedActiveEvents : this.filteredEvents;
      if (!eventsToUse || !Array.isArray(eventsToUse)) {
        return 0;
      }
      const income = eventsToUse
        .filter(event => event.type === 'CREDIT')
        .reduce((total, event) => total + parseFloat(event.amount), 0);
      return income;
    },
    filteredExpenses() {
      const eventsToUse = this.combinedActiveEvents.length > 0 ? this.combinedActiveEvents : this.filteredEvents;
      if (!eventsToUse || !Array.isArray(eventsToUse)) {
        return 0;
      }
      const expenses = eventsToUse
        .filter(event => event.type === 'DEBIT')
        .reduce((total, event) => total + parseFloat(event.amount), 0);
      return expenses;
    },
    filteredSavings() {
      const eventsToUse = this.combinedActiveEvents.length > 0 ? this.combinedActiveEvents : this.filteredEvents;
      if (!eventsToUse || !Array.isArray(eventsToUse)) {
        return 0;
      }
      const savings = eventsToUse
        .filter(event => event.category === 'SAVINGS')
        .reduce((total, event) => total + parseFloat(event.amount), 0);
      return savings;
    },
    filteredBalance() {
      return this.filteredIncome - this.filteredExpenses;
    },
    chartData() {
      return {
        labels: ['Income', 'Expenses'],
        datasets: [{
          data: [this.filteredIncome, this.filteredExpenses],
          backgroundColor: [
            '#90EE90',
            '#CD5C5C'
          ],
          borderColor: [
            '#90EE90',
            '#CD5C5C'
          ],
          borderWidth: 1
        }]
      };
    },
    expenseCategoriesData() {
      const eventsToUse = this.combinedActiveEvents.length > 0 ? this.combinedActiveEvents : this.filteredEvents;
      const categoryTotals = {};
      
      if (!eventsToUse || !Array.isArray(eventsToUse)) {
        return {
          labels: [],
          datasets: [{
            data: [],
            backgroundColor: [],
            borderColor: [],
            borderWidth: 1
          }]
        };
      }
      
      eventsToUse.forEach(event => {
        if (event.type === 'DEBIT' && event.category) {
          const catKey = Object.keys(this.categoryColors).find(
            key => key.toLowerCase() === event.category.toLowerCase()
          ) || event.category;
          const legendLabel = this.toTitleCase(catKey);
          if (!categoryTotals[legendLabel]) {
            categoryTotals[legendLabel] = 0;
          }
          categoryTotals[legendLabel] += parseFloat(event.amount);
        }
      });

      const categories = Object.keys(categoryTotals);
      const backgroundColor = [];
      const borderColor = [];

      categories.forEach((category, index) => {
        const matchKey = Object.keys(this.categoryColors).find(
          key => this.toTitleCase(key) === category
        );
        if (matchKey && this.categoryColors[matchKey]) {
          backgroundColor.push(this.categoryColors[matchKey].bg);
          borderColor.push(this.categoryColors[matchKey].border);
        } else {
          const fallbackIndex = index % this.fallbackColors.length;
          backgroundColor.push(this.fallbackColors[fallbackIndex].bg);
          borderColor.push(this.fallbackColors[fallbackIndex].border);
        }
      });

      return {
        labels: categories,
        datasets: [{
          data: Object.values(categoryTotals),
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 1
        }]
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              generateLabels: (chart) => {
                const data = chart.data;
                if (!data.labels || !data.datasets.length) return [];
                const dataset = data.datasets[0];
                return data.labels.map((label, i) => {
                  const backgroundColor = Array.isArray(dataset.backgroundColor)
                    ? dataset.backgroundColor[i]
                    : dataset.backgroundColor;
                  return {
                    text: label,
                    fillStyle: backgroundColor,
                    strokeStyle: backgroundColor,
                    fontColor: backgroundColor,
                    hidden: isNaN(dataset.data[i]) || chart.getDataVisibility(i) === false,
                    index: i
                  };
                });
              }
            }
          }
        }
      };
    }
  },
  methods: {
    ...mapActions(useProfileStore, ['fetchProfiles', 'setCurrentProfile']),
    ...mapActions(useScenariosStore, ['fetchScenarios', 'selectScenario', 'selectDefaultScenario', 'createScenario', 'deleteScenario']),
    ...mapActions(useEventsStore, ['fetchEvents', 'fetchEventsForMonthByScenario', 'fetchEventsForDateRange', 'getAllActiveScenarioEvents', 'createEvent', 'updateEvent', 'deleteEvent']),
    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
      const sidebarContainer = document.querySelector('.sidebar-container');
      if (sidebarContainer) {
        if (this.mobileSidebarOpen) {
          sidebarContainer.classList.add('open');
          document.body.classList.add('sidebar-open');
        } else {
          sidebarContainer.classList.remove('open');
          document.body.classList.remove('sidebar-open');
        }
      }
    },
    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
      const sidebarContainer = document.querySelector('.sidebar-container');
      if (sidebarContainer) {
        sidebarContainer.classList.remove('open');
      }
      document.body.classList.remove('sidebar-open');
    },
    toggleTransactionHistory() {
      this.transactionHistoryExpanded = !this.transactionHistoryExpanded;
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    },
    formatDate(dateString) {
      try {
        const date = this.parseEventDate(dateString);
        if (date.getTime() === 0) {
          return 'Invalid Date';
        }
        const month = date.getUTCMonth();
        const day = date.getUTCDate();
        const year = date.getUTCFullYear();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                           'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[month]} ${day}, ${year}`;
      } catch (error) {
        console.error('Error formatting date:', error, dateString);
        return 'Invalid Date';
      }
    },
    parseEventDate(dateString) {
      try {
        if (!dateString) {
          return new Date(0);
        }
        let date;
        if (typeof dateString === 'string') {
          if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateString.split('-').map(Number);
            date = new Date(Date.UTC(year, month - 1, day));
          } else {
            date = new Date(dateString);
          }
        } else if (dateString instanceof Date) {
          date = dateString;
        } else {
          return new Date(0);
        }
        if (isNaN(date.getTime())) {
          return new Date(0);
        }
        return date;
      } catch (error) {
        console.error('Error parsing date:', error, dateString);
        return new Date(0);
      }
    },
    hasDateRangeFilter() {
      return this.startMonth !== null && this.startDay !== null && this.startYear !== null &&
             this.endMonth !== null && this.endDay !== null && this.endYear !== null;
    },
    initializeDateRangeToCurrentMonth() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      this.startMonth = month;
      this.startDay = 1;
      this.startYear = year;
      const lastDay = new Date(year, month + 1, 0);
      this.endMonth = month;
      this.endDay = lastDay.getDate();
      this.endYear = year;
    },
    validateAndCorrectDates() {
      if (this.startMonth !== null && this.startDay !== null && this.startYear !== null) {
        const maxDaysInStartMonth = new Date(this.startYear, this.startMonth + 1, 0).getDate();
        if (this.startDay > maxDaysInStartMonth) {
          this.startDay = maxDaysInStartMonth;
        }
      }
      if (this.endMonth !== null && this.endDay !== null && this.endYear !== null) {
        const maxDaysInEndMonth = new Date(this.endYear, this.endMonth + 1, 0).getDate();
        if (this.endDay > maxDaysInEndMonth) {
          this.endDay = maxDaysInEndMonth;
        }
      }
    },
    async updateFilteredData() {
      if (this.watchersDisabled) {
        return;
      }
      await this.updateFilteredDataInternal();
    },
    async updateFilteredDataInternal() {
      const now = Date.now();
      if (now - this.lastDataUpdate < 500) {
        return;
      }
      this.lastDataUpdate = now;
      
      this.validateAndCorrectDates();
      
      if (!this.hasDateRangeFilter()) {
        this.initializeDateRangeToCurrentMonth();
      }
      
      const startDate = new Date(Date.UTC(this.startYear, this.startMonth, this.startDay));
      const endDate = new Date(Date.UTC(this.endYear, this.endMonth, this.endDay));
      await this.fetchEventsForDateRange(startDate, endDate);
      await this.updateScenarioDataInternal();
      
    },
    async updateScenarioDataInternal() {
      this.combinedActiveEvents = await this.getAllActiveScenarioEvents();
      if (this.eventsStore) {
        this.eventsStore.setFilteredEvents(this.combinedActiveEvents);
      }
      this.$forceUpdate();
      await this.$nextTick();
    },
    async getAllActiveScenarioEvents() {
      const allEvents = [];
      const seenEvents = new Set(); // Track unique events to prevent duplicates
      if (!this.currentProfile) {
        return allEvents;
      }
      const profileID = this.currentProfile.id;
      
      let startDate, endDate;
      if (this.hasDateRangeFilter()) {
        startDate = new Date(Date.UTC(this.startYear, this.startMonth, this.startDay));
        endDate = new Date(Date.UTC(this.endYear, this.endMonth, this.endDay));
      } else {
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        startDate = new Date(currentYear, currentMonth, 1);
        endDate = new Date(currentYear, currentMonth + 1, 0);
      }

      try {
        for (const scenarioId of this.activeScenarios) {
          const actualScenarioId = scenarioId === "default" ? this.selectedScenario?.id : scenarioId;
          if (!actualScenarioId) continue;
          
          const monthsToFetch = [];
          const startYear = startDate.getUTCFullYear();
          const startMonth = startDate.getUTCMonth();
          const endYear = endDate.getUTCFullYear();
          const endMonth = endDate.getUTCMonth();
          
          let currentYear = startYear;
          let currentMonth = startMonth;
          
          while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
            monthsToFetch.push({ year: currentYear, month: currentMonth });
            currentMonth++;
            if (currentMonth > 11) {
              currentMonth = 0;
              currentYear++;
            }
          }
          
          for (const { year, month } of monthsToFetch) {
            const response = await axios.get(getAPIURL()+'/api/scenario/get-events-for-scenario-for-month', {
              params: {
                scenarioID: actualScenarioId,
                profileID,
                month: month,
                year: year
              }
            });
            
            
            
            
            if (response.data && response.data.length > 0) {
              response.data.forEach((eventData) => {
                
                // Process events with occurrences
                if (eventData.occurrences && eventData.occurrences.length > 0) {
                  eventData.occurrences.forEach((occurrence) => {
                    let occurrenceDate;
                    if (typeof occurrence === 'string') {
                      if (occurrence.includes('T')) {
                        const datePart = occurrence.split('T')[0];
                        const [year, month, day] = datePart.split('-').map(Number);
                        occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                      } else {
                        const [year, month, day] = occurrence.split('-').map(Number);
                        occurrenceDate = new Date(Date.UTC(year, month - 1, day));
                      }
                    } else {
                      occurrenceDate = new Date(occurrence);
                    }
                    
                    const occurrenceDateString = occurrenceDate.toISOString().split('T')[0];
                    const startDateString = startDate.toISOString().split('T')[0];
                    const endDateString = endDate.toISOString().split('T')[0];
                    
                    if (occurrenceDateString >= startDateString && occurrenceDateString <= endDateString) {
                      // Use monthly payment for loan categories, regular amount for others
                      const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
                      // Check for both possible field names
                      const monthlyPayment = eventData.event.monthly_payment || eventData.event.monthlyPayment;
                      let displayAmount;
                      if (loanCategories.includes(eventData.event.category) && monthlyPayment && monthlyPayment > 0) {
                        // For mortgages, add escrow to the monthly payment
                        if (eventData.event.category === 'MORTGAGE' && eventData.event.escrow && eventData.event.escrow > 0) {
                          displayAmount = parseFloat(monthlyPayment) + parseFloat(eventData.event.escrow);
                        } else {
                          displayAmount = monthlyPayment;
                        }
                      } else {
                        displayAmount = eventData.event.amount;
                      }
                      
                      
                      const eventToAdd = {
                        ...eventData.event,
                        amount: displayAmount,
                        date: occurrenceDateString
                      };
                      
                      // Create a unique key for this event occurrence to prevent duplicates
                      const uniqueKey = `${eventData.event.id || eventData.event._id}-${occurrenceDateString}-${eventData.event.name || eventData.event.description || ''}`;
                      
                      if (!seenEvents.has(uniqueKey)) {
                        seenEvents.add(uniqueKey);
                        allEvents.push(eventToAdd);
                      }
                      
                    }
                  });
                } else if (eventData.event && eventData.event.start_date) {
                  // Handle events with empty occurrences but valid start dates
                  // Check if the event's start date falls within our date range
                  const eventStartDate = new Date(eventData.event.start_date);
                  const eventStartDateString = eventStartDate.toISOString().split('T')[0];
                  const startDateString = startDate.toISOString().split('T')[0];
                  const endDateString = endDate.toISOString().split('T')[0];
                  
                  if (eventStartDateString >= startDateString && eventStartDateString <= endDateString) {
                    
                    // Use monthly payment for loan categories, regular amount for others
                    const loanCategories = ['MORTGAGE', 'GENERIC_LOAN', 'AUTO_LOAN'];
                    const monthlyPayment = eventData.event.monthly_payment || eventData.event.monthlyPayment;
                    const displayAmount = loanCategories.includes(eventData.event.category) && monthlyPayment && monthlyPayment > 0 
                      ? monthlyPayment 
                      : eventData.event.amount;
                    
                    const eventToAdd = {
                      ...eventData.event,
                      amount: displayAmount,
                      date: eventStartDateString
                    };
                    
                    // Create a unique key for this event to prevent duplicates
                    const uniqueKey = `${eventData.event.id || eventData.event._id}-${eventStartDateString}-${eventData.event.name || eventData.event.description || ''}`;
                    
                    if (!seenEvents.has(uniqueKey)) {
                      seenEvents.add(uniqueKey);
                      allEvents.push(eventToAdd);
                    }
                    
                  }
                }
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching events for active scenarios:", error);
      }
      return allEvents;
    },
    async toggleScenario(scenario) {
      const wasActive = this.activeScenarios.has(scenario.id);
      
      if (wasActive) {
        this.activeScenarios.delete(scenario.id);
      } else {
        this.activeScenarios.add(scenario.id);
      }
      
      await this.updateScenarioDataInternal();
      
    },
    async initializeDashboard() {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          this.$router.push('/login');
          return;
        }

        const userID = localStorage.getItem('userID');
        if (!userID) {
          this.$router.push('/login');
          return;
        }

        await this.fetchProfiles();
        
        const userProfile = this.profiles.find(p => p.id == userID || p._id == userID);
        if (userProfile) {
          await this.profileStore.setCurrentProfile(userProfile);
        } else {
          await this.profileStore.setCurrentProfile({ id: userID });
        }
        
        await this.loadProfileDataInternal();
        
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        this.$router.push('/login');
      }
    },
    async loadProfileDataInternal() {
      try {
        if (this.currentProfile) {
          // Mark profile data as loaded
          if (!this.profileDataLoaded) {
            this.profileDataLoaded = true;
          }
          
          this.scenariosStore.setProfile(this.currentProfile);
          this.eventsStore.setProfile(this.currentProfile);
          
          await this.fetchScenarios();
          await this.selectDefaultScenario();
          
          if (!this.hasDateRangeFilter()) {
            this.initializeDateRangeToCurrentMonth();
          }
          
          if (this.hasDateRangeFilter()) {
            const startDate = new Date(Date.UTC(this.startYear, this.startMonth, this.startDay));
            const endDate = new Date(Date.UTC(this.endYear, this.endMonth, this.endDay));
            await this.eventsStore.fetchEventsForDateRange(startDate, endDate);
          } else {
            await this.fetchEventsForMonthByScenario();
          }
          
          if (!this.activeScenarios.has('default')) {
            this.activeScenarios.add('default');
          }
          
          await this.updateScenarioDataInternal();
          
          // Mark dashboard data as logged
          if (!this.dashboardDataLogged) {
            this.dashboardDataLogged = true;
          }
        }
      } catch (error) {
        console.error('Error loading profile data:', error);
      }
    },
    async handleProfileChange(newProfile) {
      // Reset the flags so we log again for the new profile
      this.profileDataLoaded = false;
      this.dashboardDataLogged = false;
      await this.loadProfileDataInternal();
    },
    async handleTransactionUpdate() {
      // Refresh dashboard data when a transaction is updated/created/deleted
      if (this.currentProfile && this.hasDateRangeFilter()) {
        const startDate = new Date(Date.UTC(this.startYear, this.startMonth, this.startDay));
        const endDate = new Date(Date.UTC(this.endYear, this.endMonth, this.endDay));
        await this.fetchEventsForDateRange(startDate, endDate);
        await this.updateScenarioDataInternal();
      } else if (this.currentProfile) {
        // If no date range is set, initialize it and refresh
        if (!this.hasDateRangeFilter()) {
          this.initializeDateRangeToCurrentMonth();
        }
        const startDate = new Date(Date.UTC(this.startYear, this.startMonth, this.startDay));
        const endDate = new Date(Date.UTC(this.endYear, this.endMonth, this.endDay));
        await this.fetchEventsForDateRange(startDate, endDate);
        await this.updateScenarioDataInternal();
      }
    },
    async selectProfile(profile) {
      const now = Date.now();
      if (now - this.lastProfileChange < 1000) {
        return;
      }
      this.lastProfileChange = now;
      
      this.loading = true;
      try {
        this.watchersDisabled = true;
        // Reset the flags so we log again for the new profile
        this.profileDataLoaded = false;
        this.dashboardDataLogged = false;
        await this.profileStore.setCurrentProfile(profile);
        await this.loadProfileDataInternal();
        this.watchersDisabled = false;
        await this.$nextTick();
      } catch (error) {
        console.error('Error selecting profile:', error);
        this.watchersDisabled = false;
      } finally {
        this.loading = false;
      }
    },
    async handleLogout() {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('userID');
        await this.profileStore.resetCurrentProfile();
      } catch (error) {
        console.error('Error during logout:', error);
        this.$router.push('/login');
      }
      this.$router.push('/login');
    },
    goToUserSettings() {
      this.$router.push('/user-settings');
    },
    goToProfileSettings() {
      this.$router.push('/profile-settings');
    },
    async resetToDefaultScenario() {
      try {
        if (this.hasScenarios) {
          await this.selectDefaultScenario();
        }
      } catch (error) {
        console.error('Error resetting to default scenario:', error);
      }
    },
    
    
    
  },
  async mounted() {
    await this.initializeDashboard();
    
    if (!this.activeScenarios.has('default')) {
      this.activeScenarios.add('default');
    }
    
    
    this.$eventBus.on('profile-changed', this.handleProfileChange);
    this.$eventBus.on('transaction-updated', this.handleTransactionUpdate);
    this.$eventBus.on('transaction-created', this.handleTransactionUpdate);
    this.$eventBus.on('transaction-deleted', this.handleTransactionUpdate);
  },
  activated() {
    this.resetToDefaultScenario();
    // Refresh dashboard data when navigating back to this page
    // Ensure we have a current profile and selected scenario, then refetch events and recompute
    if (this.currentProfile) {
      // Reinitialize date range if needed
      if (!this.hasDateRangeFilter()) {
        this.initializeDateRangeToCurrentMonth();
      }
      const startDate = new Date(Date.UTC(this.startYear ?? new Date().getFullYear(), this.startMonth ?? new Date().getMonth(), this.startDay ?? 1));
      const endDate = new Date(Date.UTC(this.endYear ?? new Date().getFullYear(), this.endMonth ?? new Date().getMonth(), this.endDay ?? new Date(this.startYear ?? new Date().getFullYear(), (this.startMonth ?? new Date().getMonth()) + 1, 0).getDate()));
      this.fetchEventsForDateRange(startDate, endDate)
        .then(() => this.updateScenarioDataInternal())
        .catch(() => {});
    }
  },
  beforeUnmount() {
    this.$eventBus.off('profile-changed', this.handleProfileChange);
    this.$eventBus.off('transaction-updated', this.handleTransactionUpdate);
    this.$eventBus.off('transaction-created', this.handleTransactionUpdate);
    this.$eventBus.off('transaction-deleted', this.handleTransactionUpdate);
  },
  watch: {
    currentProfile: {
      handler(newProfile) {
        if (this.watchersDisabled) {
          return;
        }
        // Only handle profile changes, not initial load
        if (newProfile && this.profileDataLoaded) {
          this.loadProfileDataInternal();
        } else if (!newProfile) {
          this.$router.push('/login');
        }
      },
      immediate: false
    },
    allEvents: {
      handler(newEvents) {
        if (this.watchersDisabled) return;
        
        // Only handle events changes after initial load
        if (newEvents && newEvents.length > 0 && this.profileDataLoaded &&
            (this.startMonth === null || this.startDay === null || this.startYear === null ||
             this.endMonth === null || this.endDay === null || this.endYear === null)) {
          this.initializeDateRangeToCurrentMonth();
          this.updateFilteredData();
        }
      },
      deep: true
    },
    filteredEvents: {
      handler() {
        // No action needed - just for reactivity
      },
      deep: true
    },
    startMonth: {
      handler() {
        this.validateAndCorrectDates();
      }
    },
    startYear: {
      handler() {
        this.validateAndCorrectDates();
      }
    },
    endMonth: {
      handler() {
        this.validateAndCorrectDates();
      }
    },
    endYear: {
      handler() {
        this.validateAndCorrectDates();
      }
    },
    allScenarios: {
      async handler(newScenarios) {
        if (this.watchersDisabled) {
          return;
        }
        // Only handle scenario changes after initial load
        if (newScenarios && newScenarios.length > 0 && this.profileDataLoaded) {
          if (!this.activeScenarios.has('default')) {
            this.activeScenarios.add('default');
          }
          if (this.selectedScenario) {
            await this.updateScenarioDataInternal();
          }
        }
      },
      immediate: false
    }
  }
}
</script>
